/**
 * Carte info — tout ce qui DÉCIDE d'un rendu, sans en rendre aucun.
 *
 * Deux enveloppes existent (Mantine pour le site, CSS maison pour le
 * document) ; elles n'ont le droit de différer que sur l'habillage. Chaque
 * valeur calculée à partir des props vit ici, et une seule fois : c'est ce qui
 * empêche les deux rendus de dériver.
 *
 * Aucune dépendance à Mantine, à Puck ni au store du document.
 */

import { cssLengthToPx } from '../cssLength';

/**
 * Typographie de la ligne du haut (la barre d'outils du texte riche permet de
 * déroger). `figure` est l'historique — un chiffre clé ; les trois autres
 * existent parce qu'un nom ou une citation en 2 rem gras est faux. La prop
 * reste `value` : des cartes sont en base, une prop de bloc web ne se renomme
 * pas.
 */
export const INFO_CARD_TITLE_TYPOGRAPHY = {
  figure: { fontWeight: 800, fontSize: '2rem', lineHeight: 1.15 },
  heading: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 },
  quote: { fontWeight: 400, fontSize: '1.125rem', lineHeight: 1.55 },
  'quote-xl': { fontWeight: 400, fontSize: '1.75rem', lineHeight: 1.4 },
};

/** Les valeurs proposées par les deux inspecteurs, dans le même ordre. */
export const INFO_CARD_TITLE_STYLE_OPTIONS = [
  { value: 'figure', label: 'Chiffre' },
  { value: 'heading', label: 'Titre' },
  { value: 'quote', label: 'Citation' },
  { value: 'quote-xl', label: 'Citation XXL' },
];

/** Taille du guillemet décoratif, accordée à la typographie du titre. */
const QUOTE_MARK_SIZE = {
  figure: 24, heading: 24, quote: 28, 'quote-xl': 40,
};

/** Alignement du groupe illustration + titre + description. */
const FLEX_ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };

/** Corps et largeur de la description ; la couleur atténuée est à l'enveloppe. */
export const INFO_CARD_DESCRIPTION_STYLE = { fontSize: '0.875rem', width: '100%' };

/** Hauteur du disque/carré d'illustration, en px. */
export const INFO_CARD_DEFAULT_ILLUSTRATION_PX = 52;
/** Hauteur du logo d'entreprise, en px. */
export const INFO_CARD_DEFAULT_LOGO_PX = 32;
/** Taille d'une étoile de la note, en px. */
export const INFO_CARD_DEFAULT_RATING_PX = 16;
/** Proportion du glyphe dans son disque (28/52 du design d'origine). */
export const INFO_CARD_ICON_RATIO = 0.54;
/** Couleur de l'illustration quand la carte n'en fixe pas — bleu Mantine 6,
 *  celui que `ThemeIcon color="blue"` applique côté site. */
export const INFO_CARD_DEFAULT_COLOR = '#228be6';
/** Opacité du fond de la pastille d'icône (variante « light » de Mantine). */
export const INFO_CARD_ICON_BACKGROUND_OPACITY = 0.14;

export function infoCardTitleTypography(titleStyle) {
  return INFO_CARD_TITLE_TYPOGRAPHY[titleStyle] || INFO_CARD_TITLE_TYPOGRAPHY.figure;
}

export function infoCardQuoteMarkSize(titleStyle) {
  return QUOTE_MARK_SIZE[titleStyle] || 28;
}

/** `contentAlign` → alignement flex (une valeur inconnue retombe au centre). */
export function infoCardFlexAlign(contentAlign) {
  return FLEX_ALIGN[contentAlign] || 'center';
}

/** `contentAlign` → `text-align` (même repli que l'alignement flex). */
export function infoCardTextAlign(contentAlign) {
  return FLEX_ALIGN[contentAlign] ? contentAlign : 'center';
}

export function infoCardIllustrationSizePx(illustrationSize) {
  return cssLengthToPx(illustrationSize) || INFO_CARD_DEFAULT_ILLUSTRATION_PX;
}

export function infoCardLogoHeightPx(logoHeight) {
  return cssLengthToPx(logoHeight) || INFO_CARD_DEFAULT_LOGO_PX;
}

/** Le glyphe occupe ~54 % du disque, comme au design d'origine. */
export function infoCardIconSizePx(illustrationSize) {
  return Math.round(infoCardIllustrationSizePx(illustrationSize) * INFO_CARD_ICON_RATIO);
}

/**
 * Couleur des étoiles : la sienne si elle est posée, sinon celle de
 * l'illustration — une carte réglée avant l'ajout de la prop garde son allure.
 */
export function infoCardRatingColor({ ratingColor, color } = {}) {
  return ratingColor || color || INFO_CARD_DEFAULT_COLOR;
}

export function infoCardRatingSizePx(ratingSize) {
  return cssLengthToPx(ratingSize) || INFO_CARD_DEFAULT_RATING_PX;
}

/** Note bornée et arrondie : 0 (ou une valeur absurde) ne rend rien. */
export function infoCardRatingStars(rating) {
  return Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
}

/** Espace entre les lignes : vide = collé, jamais `undefined`. */
export function infoCardLineGap(lineGap) {
  return lineGap === '' || lineGap == null ? 0 : lineGap;
}
